// src/utils/imageUpload.js
// Shared image validation + client-side compression utility.
// Logic mirrors ImpressionCardBg.jsx's compressImage — PNGs stay PNG,
// small files skip compression, resize only kicks in for very large images.
// Use this together with <ImageDropzone /> (src/components/common/ImageDropzone.jsx),
// or standalone in any other upload flow that needs the same rules.

export const MAX_IMAGE_SIZE_BYTES = 5 * 1024 * 1024; // 5MB
export const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

export class ImageValidationError extends Error {}

export function validateImageFile(file) {
    if (!file) throw new ImageValidationError('No file selected');
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
        throw new ImageValidationError('Only JPG, PNG, WEBP or GIF images are allowed');
    }
}

/**
 * Compress an image file — same behavior as ImpressionCardBg.jsx:
 * - GIFs are never re-encoded, passed through as-is
 * - Files under 2MB are passed through as-is (no compression needed)
 * - Otherwise, resize only if width/height exceeds MAX_WIDTH/MAX_HEIGHT (2000px),
 *   keeping aspect ratio
 * - PNG stays PNG (quality 1), everything else exports as JPEG (quality 0.92)
 * - High-quality canvas rendering (imageSmoothingQuality: "high")
 * Resolves with a File (original type preserved for PNG, JPEG otherwise);
 * throws ImageValidationError if it exceeds the 5MB cap after processing.
 */
export async function compressImage(file, {
    targetMaxBytes = MAX_IMAGE_SIZE_BYTES,
    smallFileThresholdBytes = 2 * 1024 * 1024, // 2MB
    maxWidth = 2000,
    maxHeight = 2000,
} = {}) {
    validateImageFile(file);

    // GIFs: don't re-encode, just enforce the size limit
    if (file.type === 'image/gif') {
        if (file.size > targetMaxBytes) {
            throw new ImageValidationError('GIF is larger than 5MB — please choose a smaller file');
        }
        return file;
    }

    // Small files: use directly, no compression
    if (file.size < smallFileThresholdBytes) {
        if (file.size > targetMaxBytes) {
            throw new ImageValidationError('Image is larger than 5MB — please choose a smaller file');
        }
        return file;
    }

    const dataUrl = await readFileAsDataURL(file);
    const img = await loadImage(dataUrl);

    let width = img.width;
    let height = img.height;

    // Resize only if very large
    if (width > maxWidth || height > maxHeight) {
        if (width > height) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
        } else {
            width = Math.round((width * maxHeight) / height);
            height = maxHeight;
        }
    }

    const outputType = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
    const quality = outputType === 'image/png' ? 1 : 0.92;

    const blob = await drawAndExport(img, width, height, outputType, quality);

    if (blob.size > targetMaxBytes) {
        throw new ImageValidationError('Could not compress image below 5MB — please choose a smaller image');
    }

    return new File([blob], file.name, { type: outputType, lastModified: Date.now() });
}

function readFileAsDataURL(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(new ImageValidationError('Could not read the selected file'));
        reader.readAsDataURL(file);
    });
}

function loadImage(src) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new ImageValidationError('Could not read the selected image'));
        img.src = src;
    });
}

function drawAndExport(img, width, height, outputType, quality) {
    return new Promise((resolve, reject) => {
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');

        // High quality rendering (important for cards/text)
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';

        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
            (blob) => (blob ? resolve(blob) : reject(new ImageValidationError('Image compression failed'))),
            outputType,
            quality
        );
    });
}